import React, { useEffect, useState } from "react";
import FacultyDashboardLayout from "../FacultyDashboardLayout";
import { toast } from "react-toastify";
import {
  getFacultyDetails,
  getStudentsBySubject,
  downloadMarksTemplate,
  uploadMarks,
  updateStudentMarksAPI,
} from "../../../utils/Api";


const MySubjects = () => {
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await getFacultyDetails();
        setSubjects(res.subjects);
      } catch (error) {
        console.error("Error fetching faculty details:", error);
        toast.error("Failed to load subjects", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    };
    fetchDetails();
  }, []);

  const handleSubjectChange = async (subjectId) => {
    const subject = subjects.find((s) => s._id === subjectId);
    setSelectedSubject(subject);
    setStudents([]);
    
    if (subject) {
      try {
        setLoading(true);
        const res = await getStudentsBySubject(subjectId);
        
        // Map students with their marks for internal components
        const studentsWithMarks = res.data.students.map((student) => {
          const subjectData = student.academicDetails.subjects.find(
            (s) => s.subject._id === subjectId,
          );
          const marks = {};
          
          // Initialize internal marks components
          marks["Viva-voce"] = subjectData?.marks.find((m) => m.schemeName === "Viva-voce")?.obtainedMarks || "";
          marks["Attendance"] = subjectData?.marks.find((m) => m.schemeName === "Attendance")?.obtainedMarks || "";
          marks["Class Participation"] = subjectData?.marks.find((m) => m.schemeName === "Class Participation")?.obtainedMarks || "";
          marks["Assignment"] = subjectData?.marks.find((m) => m.schemeName === "Assignment")?.obtainedMarks || "";
          marks["External"] = subjectData?.marks.find((m) => m.schemeName === "External")?.obtainedMarks || "";
          
          return { ...student, marks };
        });
        
        setStudents(studentsWithMarks);
      } catch (error) {
        console.error("Error fetching students:", error);
        setError("Failed to fetch students");
        
        if (error.response?.data?.message === "No batches found for this subject.") {
          toast.error("No batches found for this subject. Please contact admin to assign batches to this subject.", {
            position: "top-right",
            autoClose: 7000,
          });
        } else {
          toast.error("Failed to load students for this subject. Please try again.", {
            position: "top-right",
            autoClose: 5000,
          });
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleMarkChange = (studentId, schemeName, value) => {
    setStudents(
      students.map((student) =>
        student._id === studentId
          ? { ...student, marks: { ...student.marks, [schemeName]: value } }
          : student,
      ),
    );
  };

  const handleSaveMarks = async (studentId) => {
    const student = students.find((s) => s._id === studentId);
    const marks = ["Viva-voce", "Attendance", "Class Participation", "Assignment"]
      .map((schemeName) => ({
        schemeName,
        obtainedMarks: student.marks[schemeName] || 0,
      }))
      .filter((m) => m.obtainedMarks !== "");

    try {
      await updateStudentMarksAPI({
        studentId,
        subjectId: selectedSubject._id,
        marks,
      });
      
      toast.success("Marks saved successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      
      // Refresh the student list
      handleSubjectChange(selectedSubject._id);
    } catch (error) {
      console.error("Error saving marks:", error);
      toast.error("Failed to save marks. Please try again.", {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleDownloadTemplate = async () => {
    if (!selectedSubject) {
      toast.error("Please select a subject first.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    
    try {
      await downloadMarksTemplate(selectedSubject._id);
      toast.success("Template downloaded successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Failed to download template.", {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleFileUpload = async () => {
    if (!file || !selectedSubject) {
      toast.error("Please select a file and subject.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    
    const formData = new FormData();
    formData.append("marksSheet", file);
    
    try {
      await uploadMarks(selectedSubject._id, formData);
      toast.success("Marks uploaded successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      
      // Refresh student list
      handleSubjectChange(selectedSubject._id);
      setFile(null);
    } catch (error) {
      console.error("Error uploading marks:", error);
      toast.error("Error uploading marks. Please check the file format and try again.", {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const toggleRowExpansion = (studentId) => {
    setExpandedRows(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };

  const calculateInternalTotal = (student, internalScheme) => {
    if (!internalScheme) return 0;
    return Object.keys(internalScheme).reduce((sum, name) => {
      return sum + (parseFloat(student.marks[name]) || 0);
    }, 0);
  };

  // Determine internal marking scheme based on subject
  const getInternalMarkScheme = () => {
    if (!selectedSubject) return null;
    
    // Calculate total internal marks from marking scheme
    const totalInternal = selectedSubject.markingScheme
      .filter(scheme => scheme.name !== "External")
      .reduce((sum, scheme) => sum + scheme.value, 0);
    
    if (totalInternal === 25) {
      return {
        "Viva-voce": 5,
        "Attendance": 5,
        "Class Participation": 5,
        "Assignment": 10,
      };
    } else if (totalInternal === 40) {
      return {
        "Viva-voce": 10,
        "Attendance": 10,
        "Class Participation": 10,
        "Assignment": 10,
      };
    }
    return null;
  };

  const internalScheme = getInternalMarkScheme();

  return (
    <FacultyDashboardLayout>
      <h1 className="text-2xl font-bold mb-4">My Subjects - Internal Marks Entry</h1>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">
          Select Subject
        </label>
        <select
          value={selectedSubject?._id || ""}
          onChange={(e) => handleSubjectChange(e.target.value)}
          className="rounded-md p-2 text-lg shadow-md"
        >
          <option value="">-- Select a Subject --</option>
          {subjects.map((subject) => (
            <option key={subject._id} value={subject._id}>
              {subject.subjectName} ({subject.subjectCode})
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {loading && <p>Loading students...</p>}

      {selectedSubject && !loading && (
        <div className="my-3 flex gap-3 items-center">
          <button
            onClick={handleDownloadTemplate}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Download CSV Template
          </button>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setFile(e.target.files[0])}
            className="text-sm"
          />
          <button
            onClick={handleFileUpload}
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
          >
            Upload CSV
          </button>
        </div>
      )}

      {students.length > 0 && selectedSubject && internalScheme && (
        <div>
          <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-semibold mb-2 text-blue-800">Internal Marking Scheme:</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <div>Viva-voce: {internalScheme["Viva-voce"]} marks</div>
              <div>Attendance: {internalScheme["Attendance"]} marks</div>
              <div>Class Participation: {internalScheme["Class Participation"]} marks</div>
              <div>Assignment: {internalScheme["Assignment"]} marks</div>
            </div>
            <div className="mt-2 font-semibold">
              Total Internal: {Object.values(internalScheme).reduce((a, b) => a + b, 0)} marks
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border border-gray-300 mt-4 shadow-md rounded-lg">
              <thead>
                <tr className="bg-blue-600 text-white">
                  <th className="border border-gray-300 px-4 py-2 text-left">
                    Roll No
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left">
                    Student Name
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left">
                    Internal ({Object.values(internalScheme).reduce((a, b) => a + b, 0)})
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left">
                    External (Read-only)
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {students.map((student) => {
                  const isExpanded = expandedRows[student._id];
                  const internalTotal = calculateInternalTotal(student, internalScheme);
                  const totalInternal = Object.values(internalScheme).reduce((a, b) => a + b, 0);

                  return (
                    <React.Fragment key={student._id}>
                      <tr className="bg-white hover:bg-gray-50">
                        <td className="border border-gray-300 px-4 py-2">
                          {student.academicDetails.rollNumber}
                        </td>
                        <td className="border border-gray-300 px-4 py-2">
                          {`${student.studentDetails.firstName} ${student.studentDetails.lastName}`}
                        </td>
                        <td className="border border-gray-300 px-4 py-2">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleRowExpansion(student._id)}
                              className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-medium text-sm"
                            >
                              {isExpanded ? '▼ Hide' : '▶ Show'} Breakdown
                            </button>
                            <span className="font-semibold text-gray-700">
                              Total: {internalTotal} / {totalInternal}
                            </span>
                          </div>
                        </td>
                        <td className="border border-gray-300 px-4 py-2 bg-gray-100">
                          <input
                            type="number"
                            value={student.marks["External"]}
                            className="w-full p-2 bg-gray-200 border border-gray-400 rounded cursor-not-allowed"
                            readOnly
                            disabled
                          />
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center">
                          <button
                            onClick={() => handleSaveMarks(student._id)}
                            className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 font-medium"
                          >
                            Save
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-blue-50">
                          <td colSpan="5" className="border border-gray-300 px-4 py-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                              {Object.entries(internalScheme).map(([name, maxValue]) => (
                                <div key={name} className="flex flex-col">
                                  <label className="text-sm font-semibold text-gray-700 mb-1">
                                    {name} (Max: {maxValue})
                                  </label>
                                  <input
                                    type="number"
                                    value={student.marks[name]}
                                    onChange={(e) =>
                                      handleMarkChange(
                                        student._id,
                                        name,
                                        e.target.value,
                                      )
                                    }
                                    className="p-2 border-2 border-blue-500 rounded-md bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    min="0"
                                    max={maxValue}
                                  />
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedSubject && students.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500">
          No students found for this subject.
        </div>
      )}
    </FacultyDashboardLayout>
  );
};

export default MySubjects;
